/**
 * Shared cache invalidation utilities for SWR hooks
 */
import { mutate } from 'swr';

// Common cache key prefixes used across the application
const CACHE_KEYS = {
  CALENDAR: '/api/caregivers/me/calendar',
  AVAILABILITY: '/api/v1/me/availability-slots',
  BOOKINGS: '/api/v1/me/bookings',
  BOOKING_REQUESTS: '/api/caregivers/me/booking-requests',
} as const;

/**
 * Revalidate matching SWR keys without clearing existing cache values.
 * This keeps stale data visible if the backend is temporarily unavailable.
 */
async function revalidateMatchingCaches(
  matcher: (key: unknown) => boolean
): Promise<void> {
  await mutate(matcher, undefined, {
    revalidate: true,
    populateCache: false,
  });
}

/**
 * Invalidate all calendar caches to ensure UI consistency after changes.
 * Uses SWR's key matcher to invalidate all keys starting with the calendar prefix.
 */
export async function invalidateCalendarCaches(): Promise<void> {
  await revalidateMatchingCaches(
    key => typeof key === 'string' && key.startsWith(CACHE_KEYS.CALENDAR)
  );
}

/**
 * Invalidate all bookings caches after booking changes.
 */
export async function invalidateBookingsCaches(): Promise<void> {
  await revalidateMatchingCaches(
    key => typeof key === 'string' && key.startsWith(CACHE_KEYS.BOOKINGS)
  );
}

/**
 * Invalidate all booking requests caches after changes.
 */
export async function invalidateBookingRequestsCaches(): Promise<void> {
  await revalidateMatchingCaches(
    key => typeof key === 'string' && key.startsWith(CACHE_KEYS.BOOKING_REQUESTS)
  );
}

/**
 * Invalidate all availability caches after changes.
 */
export async function invalidateAvailabilityCaches(): Promise<void> {
  await revalidateMatchingCaches(
    key => typeof key === 'string' && key.startsWith(CACHE_KEYS.AVAILABILITY)
  );
}

/**
 * Invalidate calendar and bookings caches together.
 * Common pattern when a booking is created/canceled.
 */
export async function invalidateCalendarAndBookingsCaches(): Promise<void> {
  await Promise.all([invalidateCalendarCaches(), invalidateBookingsCaches()]);
}

/**
 * Invalidate all related caches when a booking request is accepted.
 * This can affect calendar, bookings, requests, and slot availability state.
 */
export async function invalidateAllBookingRelatedCaches(): Promise<void> {
  await Promise.all([
    invalidateCalendarCaches(),
    invalidateBookingsCaches(),
    invalidateBookingRequestsCaches(),
    invalidateAvailabilityCaches(),
  ]);
}
